/**
 * Simple Text Input with placeholder
 */

import { forwardRef } from 'react'
import styled, { css } from 'styled-components'

import { colors } from 'utils/colors'

import BaseTextInput from './BaseTextInput'
import { BaseTextInputProps } from './types'
import { Container } from './style'
import { space } from 'styled-system'

const SimpleTextInput = forwardRef<HTMLInputElement, BaseTextInputProps>(
  ({ fill = 'default', inline = false, showError = true, ...props }, ref) => (
    <Container inline={inline}>
      <TextInput ref={ref} fill={fill} inline={inline} showError={showError} {...props} />
    </Container>
  ),
)

export const getBorderColor = (props: BaseTextInputProps) => {
  if (props.error) {
    return props.errorVariant === 'warning' ? colors.yellow : colors.red
  }
  if (props.readOnly) {
    return colors.gray1
  }
  return colors.defaultBorder
}

export const getActiveStyle = (o: BaseTextInputProps) => css`
  ${(p) =>
    !o.disabled &&
    !o.readOnly &&
    `
      background-color: ${p.theme.colors.white};
      border-color: ${p.theme.colors.blue1};
      box-shadow: ${inputFocusBoxShadow};
      color: ${p.theme.colors.defaultText};
      `}
`

export const getSimpleTextInputStyle = (o: BaseTextInputProps) => css`
  box-sizing: border-box;
  background-color: ${(p) => (o.fill === 'white' ? 'white' : o.readOnly ? p.theme.colors.gray1 : inputBgColor)};
  border: 1px solid ${getBorderColor(o)};
  display: ${o.inline ? 'inline-block' : 'block'};
  padding-right: ${o.icon && o.iconPosition === 'left' ? 11 : 38}px;
  padding-left: ${o.icon && o.iconPosition === 'left' ? 38 : 11}px;
  ${space};

  ::placeholder {
    ${(p) => p.theme.font({ color: o.disabled ? 'gray4' : 'gray5' })}
  }

  &:disabled {
    ${(p) => p.theme.font({ color: o.readOnly ? 'defaultText' : 'disabledText' })}
  }

  &:hover {
    ${!o.disabled && !o.readOnly && `background-color: ${o.fill === 'white' ? 'white' : inputHoverBgColor}`}
  }

  &:active,
  &:focus {
    ${getActiveStyle(o)}
  }
`

const TextInput = styled(BaseTextInput)<BaseTextInputProps>`
  ${(p) => getSimpleTextInputStyle(p)}
`

export const inputBgColor = 'rgba(228, 233, 242, 0.2)'
export const inputFocusBoxShadow = '0 0 0 4px rgba(143, 155, 179, 0.1)'
export const inputHoverBgColor = 'rgba(228, 233, 242, 0.5)'

export default SimpleTextInput
